(() => {
  'use strict';
  if (customElements.get('aa-landing-page')) return;
  class AALandingPage extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      this._frame = requestAnimationFrame(() => {
        if (this.initialize()) return;
        this._observer = new MutationObserver(() => {
          if (this.initialize()) this._observer.disconnect();
        });
        this._observer.observe(this, {childList:true, subtree:true});
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this._frame);
      cancelAnimationFrame(this._scrollFrame);
      this._observer?.disconnect();
      this._controller?.abort();
      this._controller = null;
      this._initialized = false;
    }
    initialize() {
      if (this._initialized) return true;
      if (!this.isConnected || !this.querySelector('#sticky')) return false;
      this._initialized = true;
      const root = this;
      const find = id => root.querySelector(`#${CSS.escape(id)}`);
      const all = selector => [...root.querySelectorAll(selector)];
      const events = {signal:this._controller.signal};
      const listen = (element, type, fn, options = {}) => element?.addEventListener(type, fn, {...options, ...events});
      all('[data-review-carousel]').forEach(carousel => {
        const slides = [...carousel.querySelectorAll('[data-review-slide]')];
        const viewport = carousel.querySelector('[data-review-slides]');
        const controls = carousel.querySelector('[data-review-controls]');
        if (!viewport || !controls || slides.length < 2) return;
        let current = 0;
        const show = index => {
          current = (index + slides.length) % slides.length;
          slides.forEach((slide, position) => {
            const inactive = position !== current;
            slide.hidden = inactive;
            slide.inert = inactive;
            slide.setAttribute('aria-hidden', String(inactive));
          });
        };
        const measure = () => {
          if (events.signal.aborted) return;
          let height = 0;
          slides.forEach(slide => {
            const hidden = slide.hidden;
            slide.hidden = false;
            height = Math.max(height, slide.getBoundingClientRect().height);
            slide.hidden = hidden;
          });
          viewport.style.minHeight = `${Math.ceil(height)}px`;
        };
        show(0);
        carousel.classList.add('aa-compact-review--interactive');
        controls.querySelectorAll('.aa-compact-review__arrow').forEach(button => { button.hidden = false; });
        measure();
        let width = viewport.clientWidth;
        let measureFrame;
        const scheduleMeasure = () => {
          cancelAnimationFrame(measureFrame);
          measureFrame = requestAnimationFrame(measure);
        };
        const observer = new ResizeObserver(() => {
          if (width === viewport.clientWidth) return;
          width = viewport.clientWidth;
          scheduleMeasure();
        });
        observer.observe(viewport);
        events.signal.addEventListener('abort', () => {
          observer.disconnect();
          cancelAnimationFrame(measureFrame);
        }, {once:true});
        document.fonts?.ready.then(scheduleMeasure);
        listen(carousel.querySelector('[data-review-prev]'), 'click', () => show(current - 1));
        listen(carousel.querySelector('[data-review-next]'), 'click', () => show(current + 1));
        listen(controls, 'keydown', event => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          show(current + (event.key === 'ArrowLeft' ? -1 : 1));
        });
        let touch = null;
        listen(viewport, 'touchstart', event => {
          touch = event.touches.length === 1 ? {x:event.touches[0].clientX, y:event.touches[0].clientY} : null;
        }, {passive:true});
        listen(viewport, 'touchend', event => {
          if (!touch || !event.changedTouches.length) return;
          const dx = event.changedTouches[0].clientX - touch.x;
          const dy = event.changedTouches[0].clientY - touch.y;
          touch = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) show(current + (dx < 0 ? 1 : -1));
        }, {passive:true});
        listen(viewport, 'touchcancel', () => { touch = null; }, {passive:true});
        listen(root, 'shopify:block:select', event => {
          const index = slides.findIndex(slide => slide.dataset.reviewBlockId === event.detail?.blockId);
          if (index >= 0) show(index);
        });
      });
      const venues = [
        {name:'Losers Bar',property:'MGM Grand',area:'The Strip',note:'On the bar menu',url:'https://www.losersbarlasvegas.com/',x:174,y:424},
        {name:'Wynn Sundries',property:'Wynn Las Vegas',area:'The Strip',note:'On the shelf',url:'https://www.wynnlasvegas.com/hotel-information/retail',x:165,y:172},
        {name:'Flamingo Pool (GO Pool)',mapLabel:'Flamingo Pool',property:'Flamingo Las Vegas',area:'The Strip',note:'Pool venue',url:'https://www.caesars.com/flamingo-las-vegas/things-to-do/pools',x:174,y:300,labelSide:'left'},
        {name:'LINQ Pool (Influence, The Pool)',mapLabel:'LINQ Pool',property:'The LINQ',area:'The Strip',note:'Pool venue',url:'https://www.caesars.com/linq/things-to-do/influence-the-pool-at-the-linq',x:170,y:262,labelSide:'left'}
      ];
      const venueGrid = find('venue-grid');
      const pinLayer = find('pin-layer');
      if (venueGrid) venueGrid.innerHTML = venues.map((v,i) =>
        `<div class="vcard card" data-pin-hover="${i}" style="padding:22px 24px;display:flex;flex-direction:column;gap:6px"><div style="display:flex;justify-content:space-between;gap:10px;align-items:center;flex-wrap:wrap"><div style="display:flex;gap:12px;align-items:center"><span style="width:26px;height:26px;display:inline-flex;align-items:center;justify-content:center;background:var(--a);color:var(--a-text);font-size:13px;font-weight:800;flex:none">${i+1}</span><span class="disp" style="font-size:24px;color:#f3f3f3">${v.name}</span></div><span style="font-size:10px;font-weight:800;letter-spacing:.15em;text-transform:uppercase;color:var(--a-lite);white-space:nowrap">${v.area}</span></div><div style="font-size:14px;font-weight:600;color:#b9bdc7">${v.property}</div><div style="font-size:12px;color:#8a8f99">${v.note}</div><a href="${v.url}" target="_blank" rel="noopener" style="margin-top:10px;font-size:13px;font-weight:700;color:var(--a-lite)">Visit venue site →</a></div>`
      ).join('');
      if (pinLayer) {
        pinLayer.innerHTML = venues.map((v,i) => `<a class="pin" data-pin="${i}" href="${v.url}" target="_blank" rel="noopener" aria-label="${v.name} at ${v.property}"><circle class="halo" cx="${v.x}" cy="${v.y}" r="17" fill="var(--a)" opacity=".18"></circle><circle class="dot" cx="${v.x}" cy="${v.y}" r="10" fill="var(--a)"></circle><text x="${v.x}" y="${v.y+3.5}" text-anchor="middle" font-size="10" font-weight="800" fill="var(--a-text)" style="pointer-events:none">${i+1}</text><text x="${v.labelSide==='left'?v.x-22:v.x+22}" y="${v.y+4}" text-anchor="${v.labelSide==='left'?'end':'start'}" font-size="11" font-weight="700" fill="#b9bdc7" letter-spacing="1" style="pointer-events:none;text-transform:uppercase">${v.mapLabel||v.name}</text></a>`).join('');
        all('[data-pin-hover]').forEach(card => {
          const pin = pinLayer.querySelector(`[data-pin="${card.dataset.pinHover}"]`);
          listen(card,'mouseenter',() => pin?.classList.add('hot'));
          listen(card,'mouseleave',() => pin?.classList.remove('hot'));
        });
      }
      const flavorData = {};
      ['bo','sl'].forEach(flavor => {
        const button = root.querySelector(`.heroMedia [data-fbtn="${flavor}"]`);
        const data = button?.dataset || {};
        flavorData[flavor] = {
          id:data.variantId || '',price:data.price || '',currencyPrice:data.priceCurrency || '',
          available:data.available === 'true',min:Number(data.min) || 1,
          max:data.max ? Number(data.max) : 24,step:Number(data.step) || 1,
          name:flavor === 'sl' ? 'Slayberry' : 'Boujee Berry'
        };
      });
      let flavor = 'bo';
      let quantity = 1;
      let heroReady = false;
      let shopReady = false;
      const buyHero = find('buy-hero');
      const buyShop = find('buy-shop');
      const buySticky = find('buy-sticky');
      const heroVariant = root.querySelector('[data-hero-12-variant]');
      const normalizeQuantity = selected => {
        const max = Math.max(selected.min, Math.min(24,selected.max));
        quantity = Math.max(selected.min, Math.min(max,quantity));
        quantity = selected.min + Math.floor((quantity-selected.min)/selected.step)*selected.step;
      };
      const sync = () => {
        const selected = flavorData[flavor];
        normalizeQuantity(selected);
        root.classList.toggle('slay',flavor === 'sl');
        all('[data-fbtn]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.fbtn === flavor)));
        all('[data-img-bo]').forEach(image => image.style.opacity = flavor === 'bo' ? '1' : '0');
        all('[data-img-sl]').forEach(image => image.style.opacity = flavor === 'sl' ? '1' : '0');
        find('aa-variant').value = selected.id;
        heroVariant.value = selected.id;
        find('aa-qty-input').value = String(quantity);
        find('qty-n').textContent = String(quantity);
        shopReady = !!selected.id && selected.available;
        heroReady = shopReady && selected.min <= 1 && selected.max >= 1;
        const unavailable = selected.available ? root.dataset.unavailableLabel : root.dataset.soldoutLabel;
        buyHero.disabled = !heroReady;
        buyHero.textContent = heroReady ? buyHero.dataset.buyLabel : unavailable;
        buyShop.disabled = buySticky.disabled = !shopReady;
        const buyLabel = quantity > 1 ? buyShop.dataset.buyManyLabel.replaceAll('{quantity}',String(quantity)) : buyShop.dataset.buyLabel;
        buyShop.textContent = buySticky.textContent = shopReady ? buyLabel : unavailable;
        find('aa-hero-12-note').textContent = heroReady ? `${selected.name} · ${selected.currencyPrice} · ${root.dataset.packBottles}` : `${selected.name} · ${root.dataset.heroUnavailable}`;
        find('aa-shop-price').textContent = selected.price;
        find('sticky-flavor').textContent = selected.name;
        find('aa-sticky-price').textContent = selected.price;
        find('qty-dn').disabled = quantity <= selected.min;
        find('qty-up').disabled = quantity + selected.step > Math.min(24,selected.max);
        root.dispatchEvent(new CustomEvent('aa:flavor-change',{detail:{flavor}}));
      };
      listen(root,'click',event => {
        const button = event.target.closest?.('[data-fbtn]');
        if (!button || !root.contains(button)) return;
        flavor = button.dataset.fbtn === 'sl' ? 'sl' : 'bo';
        sync();
      });
      listen(find('qty-up'),'click',() => {quantity += flavorData[flavor].step;sync();});
      listen(find('qty-dn'),'click',() => {quantity -= flavorData[flavor].step;sync();});
      listen(find('aa-hero-12-form'),'submit',event => {sync();if (!heroReady) event.preventDefault();});
      listen(find('aa-buy-form'),'submit',event => {sync();if (!shopReady) event.preventDefault();});
      const burger = find('burger');
      const mobilePanel = find('mpanel');
      listen(burger,'click',() => {
        const open = mobilePanel.classList.toggle('open');
        burger.setAttribute('aria-expanded',String(open));
      });
      mobilePanel?.querySelectorAll('a').forEach(link => listen(link,'click',() => {
        mobilePanel.classList.remove('open');
        burger.setAttribute('aria-expanded','false');
      }));
      const comparison = find('cmp');
      const night = find('cmp-night');
      const handle = find('cmp-handle');
      if (comparison && night && handle) {
        let value = 50;
        let dragging = false;
        const setComparison = next => {
          value = Math.max(4,Math.min(96,next));
          night.style.clipPath = `inset(0 ${100-value}% 0 0)`;
          handle.style.left = `${value}%`;
          comparison.setAttribute('aria-valuenow',String(Math.round(value)));
        };
        const fromPointer = event => {
          const box = comparison.getBoundingClientRect();
          if (box.width) setComparison((event.clientX-box.left)/box.width*100);
        };
        listen(comparison,'pointerdown',event => {
          if (event.button !== 0) return;
          event.preventDefault();dragging = true;
          comparison.setPointerCapture(event.pointerId);fromPointer(event);
        });
        listen(comparison,'pointermove',event => {if (dragging) fromPointer(event);});
        listen(comparison,'pointerup',() => {dragging = false;});
        listen(comparison,'pointercancel',() => {dragging = false;});
        listen(comparison,'keydown',event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          setComparison(event.key === 'Home' ? 4 : event.key === 'End' ? 96 : value + (event.key === 'ArrowRight' ? 5 : -5));
        });
        setComparison(50);
      }
      all('.ing').forEach(button => listen(button,'click',() => {
        all('.ing').forEach(other => other.setAttribute('aria-pressed',String(other === button)));
        const support = button.classList.contains('sup');
        const tag = find('ing-tag');
        tag.textContent = button.dataset.tag;
        tag.style.background = support ? 'rgba(185,189,199,.15)' : 'var(--a-15)';
        tag.style.color = support ? '#b9bdc7' : 'var(--a-lite)';
        find('ing-name').textContent = button.textContent;
        find('ing-copy').textContent = button.dataset.copy;
      }));
      const sticky = find('sticky');
      const hero = find('hero');
      const shop = find('shop');
      const updateSticky = () => {
        this._scrollFrame = null;
        let show = window.scrollY > hero.offsetHeight + 120;
        if (show) {
          const box = shop.getBoundingClientRect();
          if (box.top < window.innerHeight && box.bottom > 0) show = false;
        }
        sticky.classList.toggle('show',show);
      };
      listen(window,'scroll',() => {
        if (!this._scrollFrame) this._scrollFrame = requestAnimationFrame(updateSticky);
      },{passive:true});
      listen(window,'resize',updateSticky,{passive:true});
      listen(document,'shopify:block:select',event => {
        if (root.contains(event.target)) event.target.scrollIntoView({block:'center',behavior:'auto'});
      });
      sync();updateSticky();
      return true;
    }
  }
  customElements.define('aa-landing-page',AALandingPage);
})();
